import {
    create_criterion,
    delete_criterion,
    generate_prompt_from_title,
    get_all_criteria,
    update_criterion
} from "../Services/critere.service";
import { CritereActionTypes } from "./actionTypes";
import { message } from "antd";
import i18n from "../i18n"; // ajout pour la traduction

const extractCriterionErrorMessage = (error, fallback) => {
    const raw = error?.response?.data || error || {};
    const detail = raw?.detail || raw?.data || raw;
    const commercialMessage = detail?.message || raw?.message;

    if (typeof detail === "string") {
        return detail;
    }

    if (typeof commercialMessage === "string") {
        return commercialMessage;
    }

    if (commercialMessage && typeof commercialMessage === "object") {
        const language = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("fr") ? "fr" : "en";
        const preferredBody = commercialMessage[`default_body_${language}`];
        const preferredTitle = commercialMessage[`default_title_${language}`];
        return (
            preferredBody ||
            preferredTitle ||
            commercialMessage.default_body_fr ||
            commercialMessage.default_body_en ||
            commercialMessage.default_title_fr ||
            commercialMessage.default_title_en ||
            fallback
        );
    }

    return raw?.error || raw?.message || error?.message || fallback;
};

const buildFetchResponseError = async (res, fallback) => {
    let payload = null;

    try {
        payload = await res.clone().json();
    } catch (_jsonError) {
        try {
            const text = await res.clone().text();
            payload = text?.trim() || null;
        } catch (_textError) {
            payload = null;
        }
    }

    const errorMessage = extractCriterionErrorMessage(
        payload || { message: res.statusText },
        fallback,
    );
    const error = new Error(errorMessage);
    error.response = { data: payload };
    return error;
};

export function RequestGeneratePrompt(title, description, onStreamUpdate, onDone, onError) {
    return async function (dispatch) {
        try {
            const res = await generate_prompt_from_title(title, description);
            if (!res.ok) {
                throw await buildFetchResponseError(res, i18n.t("criteria:messages.server"));
            }
            if (!res.body) {
                throw new Error(i18n.t("criteria:messages.server"));
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let fullPrompt = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                fullPrompt += chunk;
                onStreamUpdate(fullPrompt);
            }

            onDone && onDone();
        } catch (err) {
            const errorMessage = extractCriterionErrorMessage(err, i18n.t("criteria:messages.server"));
            message.error(errorMessage);
            dispatch(setCriterionError(errorMessage));
            onError && onError(errorMessage);
        }
    };
}

export function RequestGetAllCriteria() {
    return async function (dispatch) {
        dispatch(setCriterionLoading(true));
        try {
            const res = await get_all_criteria();
            if (res.success && res.data) {
                dispatch(setCriteria(res.data));
            } else {
                dispatch(setCriterionError(extractCriterionErrorMessage(res, i18n.t("criteria:messages.loadError"))));
            }
        } catch (err) {
            dispatch(setCriterionError(extractCriterionErrorMessage(err, i18n.t("criteria:messages.server"))));
        } finally {
            dispatch(setCriterionLoading(false));
        }
    };
}

export function RequestCreateCriterion(data) {
    return async function (dispatch) {
        dispatch(setCriterionLoading(true));
        try {
            const res = await create_criterion(data);
            if (res.success) {
                dispatch(addCriterion(res.data));
                message.success(i18n.t("criteria:messages.created"));
                return { success: true, data: res.data };
            } else {
                const errorMessage = extractCriterionErrorMessage(res, i18n.t("criteria:messages.unknown"));
                message.error(errorMessage);
                dispatch(setCriterionError(errorMessage));
                return { success: false, error: errorMessage };
            }
        } catch (err) {
            const errorMessage = extractCriterionErrorMessage(err, i18n.t("criteria:messages.server"));
            message.error(errorMessage);
            dispatch(setCriterionError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setCriterionLoading(false));
        }
    };
}

export function RequestUpdateCriterion(id, data) {
    return async function (dispatch) {
        dispatch(setCriterionLoading(true));
        try {
            const res = await update_criterion(id, data);
            if (res.success) {
                dispatch(updateCriterion(res.data));
                message.success(i18n.t("criteria:messages.updated"));
                return { success: true, data: res.data };
            } else {
                const errorMessage = extractCriterionErrorMessage(res, i18n.t("criteria:messages.unknown"));
                message.error(errorMessage);
                dispatch(setCriterionError(errorMessage));
                return { success: false, error: errorMessage };
            }
        } catch (err) {
            const errorMessage = extractCriterionErrorMessage(err, i18n.t("criteria:messages.server"));
            message.error(errorMessage);
            dispatch(setCriterionError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setCriterionLoading(false));
        }
    };
}

export function RequestDeleteCriterion(id) {
    return async function (dispatch) {
        dispatch(setCriterionLoading(true));
        try {
            const res = await delete_criterion(id);
            if (res.success) {
                dispatch(deleteCriterion(id));
                message.success(i18n.t("criteria:messages.deleted"));
                return { success: true };
            } else {
                const errorMessage = extractCriterionErrorMessage(res, i18n.t("criteria:messages.unknown"));
                message.error(errorMessage);
                dispatch(setCriterionError(errorMessage));
                return { success: false, error: errorMessage };
            }
        } catch (err) {
            const errorMessage = extractCriterionErrorMessage(err, i18n.t("criteria:messages.server"));
            message.error(errorMessage);
            dispatch(setCriterionError(errorMessage));
            return { success: false, error: errorMessage };
        } finally {
            dispatch(setCriterionLoading(false));
        }
    };
}

export const setCriteria = (criteria) => ({
    type: CritereActionTypes.SET_DATA,
    payload: criteria,
});

export const addCriterion = (criterion) => ({
    type: CritereActionTypes.ADD_ONE,
    payload: criterion,
});

export const updateCriterion = (criterion) => ({
    type: CritereActionTypes.UPDATE_ONE,
    payload: criterion,
});

export const deleteCriterion = (id) => ({
    type: CritereActionTypes.DELETE_ONE,
    payload: id,
});

export const setCriterionLoading = (isLoading) => ({
    type: CritereActionTypes.SET_LOADING,
    payload: isLoading,
});

export const setCriterionError = (error) => ({
    type: CritereActionTypes.SET_ERROR,
    payload: error,
});
